import { select } from 'd3-selection';
import { grantsFeature, type Entitlements, type RenderContext } from '@chartforge/shared-types';

/**
 * Engine-level watermark. Rendered on top of the chart SVG whenever the
 * RenderContext carries a watermark (e.g. trial plans). Charts themselves
 * never implement watermarking — it is centralized here so every current and
 * future chart is watermarked consistently.
 */

/** Default overlay text. */
export const WATERMARK_TEXT = 'Charts by Nex Flow Automation Trial';

/** Default overlay opacity — low enough to read the chart through it. */
export const WATERMARK_OPACITY = 0.14;

/** Rotation, in degrees (negative = counter-clockwise). */
export const WATERMARK_ROTATION = -20;

export interface WatermarkOptions {
  text?: string;
  opacity?: number;
}

/**
 * The watermark a set of entitlements implies.
 *
 * Capability-driven, not plan-driven: the `branding` capability is what removes
 * the watermark, and `branding` is granted by both paid plans (the M4 exit
 * criterion is "trial watermark + limited options ... and a paid upgrade
 * removing both"). This is the ONLY place that decision is made for every host,
 * so Excel, Sheets and Power BI cannot disagree.
 */
export function watermarkForEntitlements(
  entitlements: Entitlements,
  options: WatermarkOptions = {}
): { text: string; opacity: number } | null {
  if (grantsFeature(entitlements, 'branding')) {
    return null;
  }
  return {
    text: options.text ?? WATERMARK_TEXT,
    opacity: options.opacity ?? WATERMARK_OPACITY
  };
}

export function renderWatermark(ctx: RenderContext): void {
  if (!ctx.watermark) {
    return;
  }
  const svg = select(ctx.container).select<SVGSVGElement>('svg.cf-chart');
  if (svg.empty()) {
    return;
  }
  svg.selectAll('g.cf-watermark').remove();

  const { width, height } = ctx;
  const cx = width / 2;
  const cy = height / 2;

  const g = svg
    .append('g')
    .attr('class', 'cf-watermark')
    .attr('pointer-events', 'none')
    .attr('aria-hidden', 'true');

  g.append('text')
    .attr('x', cx)
    .attr('y', cy)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('transform', `rotate(${WATERMARK_ROTATION} ${cx} ${cy})`)
    .attr('fill', '#6b7280')
    .attr('fill-opacity', ctx.watermark.opacity)
    .attr('font-size', Math.max(20, Math.min(width, height) / 10))
    .attr('font-weight', 700)
    .text(ctx.watermark.text);
}

/**
 * True when a rendered SVG carries the trial watermark.
 *
 * Hosts use it for export-time assertions and diagnostics ("is this artifact
 * watermarked?"), which is how the M4 exit criterion is verified across all
 * three hosts.
 */
export function svgHasWatermark(svg: SVGSVGElement | null): boolean {
  return Boolean(svg?.querySelector('g.cf-watermark text'));
}
